import { forwardRef, useId } from 'react';
import * as Label from '@radix-ui/react-label';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

/*
  Multi-line input, wired the same way Field is: a real <label> bound by htmlFor, hint and
  error both listed in aria-describedby so the error does not replace the hint, aria-invalid
  driven by the error prop, and the error text in a live region so a server side rejection
  arriving after submit is announced without stealing focus.

  Kept as a separate component rather than a mode on Field, because the two differ in more
  than the tag: a textarea takes Enter as a newline rather than as submit, which changes how
  a form around it has to be built, and rows is not an input concept.
*/

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly labelHidden?: boolean | undefined;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, labelHidden = false, className, required, rows = 3, ...rest },
  ref,
) {
  const uid = useId();
  const fieldId = `textarea-${uid}`;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;

  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <Label.Root
        htmlFor={fieldId}
        className={cn(
          'text-xs font-semibold tracking-wide text-fg-muted uppercase',
          labelHidden && 'sr-only',
        )}
      >
        {label}
        {required === true ? (
          <span className="ml-1 text-[var(--color-danger)]" aria-hidden="true">
            *
          </span>
        ) : null}
      </Label.Root>

      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        required={required}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        className={cn(
          'w-full resize-y rounded-[var(--radius-control)] border bg-surface px-3 py-2 text-sm text-fg',
          'placeholder:text-fg-subtle',
          error === undefined
            ? 'border-border hover:border-border-strong'
            : 'border-[var(--color-danger)]',
          className,
        )}
        {...rest}
      />

      {hint !== undefined ? (
        <p id={hintId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}

      <p
        id={errorId}
        role="alert"
        aria-live="polite"
        className={cn(
          'flex items-start gap-1.5 text-xs font-medium text-[var(--color-danger)]',
          error === undefined && 'hidden',
        )}
      >
        {error === undefined ? null : (
          <>
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </>
        )}
      </p>
    </div>
  );
});
